import { Injectable } from '@angular/core';
import {
	PluginType,
	PluginMiniCryptoGameData,
	CryptoLevel,
	CryptoWord,
	PluginInterfaceId,
} from '@meta-press/shared-plugins';
import {
	GenerationRequest,
	GenerationResult,
	GenerationPrompts,
	ValidationResult,
} from './base/game-generation.interface';
import { BaseGenerationService } from './base/base-generation.service';
import { SettingsService } from '../settings-service/settings.service';

/**
 * Mini-crypto specific generation service
 */
@Injectable({
	providedIn: 'root',
})
export class MiniCryptoGenerationService extends BaseGenerationService<PluginMiniCryptoGameData> {
	readonly pluginType = PluginType.MiniCrypto;

	/**
	 * Generate number mapping for a word using provided letter-to-number map
	 */
	private generateMappingForWord(word: string, letterToNumber: Map<string, number>): number[] {
		const upperWord = word.toUpperCase();
		const mapping: number[] = [];

		for (let i = 0; i < upperWord.length; i++) {
			const letter = upperWord[i];
			const number = letterToNumber.get(letter);

			if (number === undefined) {
				// Letter not in map, use 0 as fallback
				console.warn(`[MiniCryptoGenerationService] Letter "${letter}" not found in mapping, using 0`);
				mapping.push(0);
			} else {
				mapping.push(number);
			}
		}

		return mapping;
	}

	/**
	 * Generate randomized mappings for all words in a level
	 * Collects unique letters and assigns random numbers to prevent alphabet counting cheats
	 */
	private generateMappingsForLevel(level: CryptoLevel): void {
		if (!level.words || level.words.length === 0) return;

		// Collect all unique letters used in this level
		const uniqueLetters = new Set<string>();
		for (const word of level.words) {
			if (word.word) {
				const upperWord = word.word.toUpperCase();
				for (let i = 0; i < upperWord.length; i++) {
					uniqueLetters.add(upperWord[i]);
				}
			}
		}

		// Create array of numbers 1 to N where N is the number of unique letters
		const numbers = Array.from({ length: uniqueLetters.size }, (_, i) => i + 1);

		// Shuffle the numbers to create random assignment
		for (let i = numbers.length - 1; i > 0; i--) {
			const j = Math.floor(Math.random() * (i + 1));
			[numbers[i], numbers[j]] = [numbers[j], numbers[i]];
		}

		// Create letter-to-number mapping
		const letterToNumber = new Map<string, number>();
		const lettersArray = Array.from(uniqueLetters);
		for (let i = 0; i < lettersArray.length; i++) {
			letterToNumber.set(lettersArray[i], numbers[i]);
		}

		// Apply mapping to all words
		for (const word of level.words) {
			if (word.word) {
				word.mapping = this.generateMappingForWord(word.word, letterToNumber);
			}
		}
	}

	/**
	 * Create system and user prompts for mini-crypto generation
	 */
	createPrompts(
		request: GenerationRequest,
		settingsService: SettingsService,
	): GenerationPrompts {
		const { parameters, sourceMaterial } = request;
		const levelCount = parameters['levelCount'] || 3;
		const wordHintMode = parameters['wordHintMode'] || 'first';

		const targetLanguagePrompt = settingsService.GetTargetLanguagePrompt();

		// Build word hint instructions based on mode
		let hintInstructions = '';
		switch (wordHintMode) {
			case 'none':
				hintInstructions = '- ÄLÄ luo sanavihjeitä. Jätä hint-kenttä tyhjäksi kaikille sanoille.';
				break;
			case 'first':
				hintInstructions = '- Ensimmäisellä sanalla jokaisessa tasossa on tekstivihje (hint-kenttä täytetty). Muilla sanoilla hint-kenttä tyhjä.';
				break;
			case 'all':
				hintInstructions = '- KAIKILLA sanoilla on tekstivihje (hint-kenttä täytetty jokaiselle sanalle).';
				break;
			case 'random':
				hintInstructions = '- Yhdellä satunnaisella sanalla jokaisessa tasossa on tekstivihje. Muilla sanoilla hint-kenttä tyhjä.';
				break;
		}

		// Build difficulty progression rules dynamically
		const difficultyRules: string[] = [];
		for (let i = 1; i <= levelCount; i++) {
			const wordCount = 2 + i; // Level 1: 3 words, Level 2: 4 words, etc.
			const minLength = 4 + (i - 1); // Level 1: 4-6, Level 2: 5-7, etc.
			const maxLength = 6 + (i - 1);
			difficultyRules.push(
				`- Taso ${i}: ${wordCount} sanaa (${minLength}-${maxLength} kirjainta)`
			);
		}

		let systemPrompt: string;
		let userPrompt: string;

		if (parameters['theme']?.trim()) {
			// Theme-based generation
			const theme = parameters['theme'];

			systemPrompt = `Olet kryptogrammipulmapelien luoja. Tehtäväsi on luoda Mini-krypto -peli, jossa pelaaja ratkaisee sanoja numerovihjeiden avulla.

HUOMAA: Sinun EI tarvitse luoda numerovihjeitä - ne luodaan automaattisesti kirjaimista! Keskity vain luomaan hyviä sanoja teeman mukaan.

SÄÄNNÖT:
- Luo ${levelCount} tasoa. Kussakin tasossa on edellistä tasoa korkeampi vaikeustaso
${difficultyRules.join('\n')}
- Kaikki sanat samasta teemasta
${hintInstructions}
- TÄRKEÄÄ: Vaikeus kasvaa lisäämällä sanoja JA valitsemalla pitempiä sekä monimutkaisempia sanoja

${targetLanguagePrompt}`;

			userPrompt = `Luo Mini-krypto -peli teemalla "${theme}".

TÄRKEÄÄ: Palauta JSON-objekti, jossa on KAIKKI seuraavat kentät:
{
	"title": "${theme}",
	"description": "Täytä kaikki kirjainkentät. Sama numero = sama kirjain!",
	"levels": [
		{
			"words": [
				{
					"word": "SANA",
					"hint": "${wordHintMode === 'none' ? '' : 'Vihjeteksti sanalle'}"
				}
			]
		}
	]
}

HUOMAA:
- "title"-kentässä tulee olla vain teeman nimi (esim. "Eläimet")
- "description" voi olla vakioteksti tai teemaan liittyvä ohje
- ÄLÄ sisällytä "mapping"-kenttää - se luodaan automaattisesti!
- Keskity luomaan hyviä, teeman mukaisia sanoja

HINT-KENTTÄ:
${hintInstructions}

Tee sanat teeman mukaisiksi!`;
		} else {
			// Source material-based generation
			systemPrompt = `Olet kryptogrammipulmapelien luoja. Tehtäväsi on luoda Mini-krypto -peli, jossa pelaaja ratkaisee sanoja numerovihjeiden avulla.

HUOMAA: Sinun EI tarvitse luoda numerovihjeitä - ne luodaan automaattisesti kirjaimista! Keskity vain luomaan hyviä sanoja lähdemateriaaleista.

Selvitä, mistä lähdemateriaalissa on kyse ja poimi sieltä keskeisiä sanoja Mini-krypto -peliä varten.

SÄÄNNÖT:
- Luo ${levelCount} tasoa. Kussakin tasossa on edellistä tasoa korkeampi vaikeustaso
${difficultyRules.join('\n')}
- Kaikki sanat valitaan lähdemateriaalista
${hintInstructions}
- TÄRKEÄÄ: Vaikeus kasvaa lisäämällä sanoja JA valitsemalla pitempiä sekä monimutkaisempia sanoja
- Valitse sanoja, jotka liittyvät lähdemateriaalin keskeisiin aiheisiin

${targetLanguagePrompt}`;

			userPrompt = `Luo Mini-krypto -peli valitusta lähdemateriaalista.

TÄRKEÄÄ: Palauta JSON-objekti, jossa on KAIKKI seuraavat kentät:
{
	"title": "Lähdemateriaalin pääaihe",
	"description": "Täytä kaikki kirjainkentät. Sama numero = sama kirjain!",
	"levels": [
		{
			"words": [
				{
					"word": "SANA",
					"hint": "${wordHintMode === 'none' ? '' : 'Vihjeteksti sanalle'}"
				}
			]
		}
	]
}

HUOMAA:
- "title"-kentässä tulee olla lähdemateriaalin pääaihe tai keskeinen teema
- "description" voi olla vakioteksti tai aiheeseen liittyvä ohje
- ÄLÄ sisällytä "mapping"-kenttää - se luodaan automaattisesti!
- Valitse sanoja, jotka ovat keskeisiä lähdemateriaalin ymmärtämisen kannalta
- Perusta sanat lähdemateriaalin faktaisiin tietoihin

HINT-KENTTÄ:
${hintInstructions}

Poimi sanat lähdemateriaalista!`;
		}

		return { system: systemPrompt, user: userPrompt };
	}

	/**
	 * Process AI response into PluginMiniCryptoGameData
	 */
	protected processResponseCore(
		response: any,
		parameters: any,
	): GenerationResult {
		try {
			const json = response.json;

			if (!json || !json.levels || !Array.isArray(json.levels)) {
				return {
					success: false,
					error: 'Invalid response: missing levels array',
					rawResponse: response.rawResponse,
				};
			}

			// Generate mappings programmatically for all levels
			// This ensures 100% consistency as mappings are deterministic
			for (let i = 0; i < json.levels.length; i++) {
				this.generateMappingsForLevel(json.levels[i]);
			}

			// Validate each level structure
			for (let i = 0; i < json.levels.length; i++) {
				const level = json.levels[i];
				if (!level.words || !Array.isArray(level.words)) {
					return {
						success: false,
						error: `Level ${i + 1} is missing words array`,
						rawResponse: response.rawResponse,
					};
				}

				// Validate each word has required fields
				for (let j = 0; j < level.words.length; j++) {
					const word = level.words[j];
					if (!word.word) {
						return {
							success: false,
							error: `Level ${i + 1}, Word ${j + 1} is missing word`,
							rawResponse: response.rawResponse,
						};
					}

					// Mapping should now exist (generated above)
					if (!word.mapping || word.word.length !== word.mapping.length) {
						return {
							success: false,
							error: `Level ${i + 1}, Word ${j + 1}: failed to generate valid mapping`,
							rawResponse: response.rawResponse,
						};
					}
				}
			}

			// Create game data
			const gameData = new PluginMiniCryptoGameData();
			gameData.title = json.title || 'Mini-krypto';
			gameData.description = json.description || 'Täytä kaikki kirjainkentät. Sama numero = sama kirjain!';
			gameData.levels = json.levels;

			// Apply hint settings from parameters
			gameData.useHints = parameters['useHints'] !== false; // Default true
			gameData.totalHints = parameters['hintsTotal'] || 2;
			gameData.hintsRemaining = gameData.totalHints;
			gameData.selectedInterfaceId = PluginInterfaceId.Interface1;

			return {
				success: true,
				gameData,
				rawResponse: response.rawResponse,
			};
		} catch (error) {
			return {
				success: false,
				error: `Response processing failed: ${error}`,
				rawResponse: response.rawResponse,
			};
		}
	}

	/**
	 * Validate mini-crypto specific request
	 */
	validateRequest(request: GenerationRequest): ValidationResult {
		// For source material generation
		const hasSourceMaterial =
			request.sourceMaterial &&
			request.sourceMaterial.fileHandles.length > 0;

		// For topic-based generation
		const hasTheme = request.parameters['theme'] &&
			request.parameters['theme'].trim().length > 0;

		if (!hasSourceMaterial && !hasTheme) {
			return {
				valid: false,
				error: 'Anna teema tai valitse lähdemateriaali',
			};
		}

		const levelCount = request.parameters['levelCount'] || 3;
		if (levelCount < 1 || levelCount > 5) {
			return {
				valid: false,
				error: 'Tasojen määrän tulee olla 1-5',
			};
		}

		return { valid: true };
	}

	/**
	 * Get default parameters for mini-crypto
	 */
	getDefaultParameters(): any {
		return {
			theme: '', // Empty by default - allows source material to be used when available
			levelCount: 3,
			wordHintMode: 'first',
			useHints: true,
			hintsTotal: 2,
			clearPrevious: false,
		};
	}

	/**
	 * Merge new content with existing game data based on parameters
	 */
	override mergeContent(
		existingGameData: PluginMiniCryptoGameData,
		newGameData: PluginMiniCryptoGameData,
		clearPrevious: boolean,
	): PluginMiniCryptoGameData {
		if (clearPrevious) {
			// Replace all content
			existingGameData.title = newGameData.title;
			existingGameData.description = newGameData.description;
			existingGameData.levels = newGameData.levels;
			existingGameData.totalHints = newGameData.totalHints;
			existingGameData.hintsRemaining = newGameData.hintsRemaining;
		} else {
			// Append new levels
			if (newGameData.levels && newGameData.levels.length > 0) {
				if (!existingGameData.levels) {
					existingGameData.levels = [];
				}
				existingGameData.levels.push(...newGameData.levels);
			}

			// Update title if empty
			if (!existingGameData.title && newGameData.title) {
				existingGameData.title = newGameData.title;
			}

			// Update description if empty
			if (!existingGameData.description && newGameData.description) {
				existingGameData.description = newGameData.description;
			}
		}

		return existingGameData;
	}
}
